import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
    clearStoredDispatchAuthData,
    clearStoredSuperAdminAuthData,
    clearAllAuthSessionFlags,
    clearSuperAdminSessionActive,
    getStoredAuthToken,
    getStoredSuperAdminToken,
    getStoredUser,
    hasActiveDispatchSession,
    markDispatchSessionActive,
    markSuperAdminSessionActive
} from '../utils/authSession';
import { setStoredAuthToken, setStoredUser } from '../utils/authSession';

const AuthContext = createContext();

const clearImpersonationSession = () => {
    try {
        sessionStorage.removeItem('impersonating');
        sessionStorage.removeItem('impersonating_name');
    } catch {}
};

const normalizeToken = (rawToken) => {
    if (!rawToken) return null;
    const token = String(rawToken).trim();
    if (!token) return null;
    if (token === 'null' || token === 'undefined') return null;
    if (!/^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(token)) return null;
    return token;
};

const decodeJwtPayload = (token) => {
    try {
        const payloadPart = String(token || '').split('.')[1];
        if (!payloadPart) return null;
        const json = atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(json);
    } catch {
        return null;
    }
};

const isExpiredToken = (token) => {
    const payload = decodeJwtPayload(token);
    const exp = Number(payload?.exp || 0);
    if (!payload || !exp) return true;
    return Date.now() >= exp * 1000;
};

const isRequestTimeout = (error) => {
    const code = String(error?.code || '').trim().toUpperCase();
    const message = String(error?.message || '').trim().toUpperCase();
    return code === 'ECONNABORTED' || message.includes('TIMEOUT');
};

const isAuthFailure = (error) => {
    const status = Number(error?.response?.status || 0);
    return status === 401 || status === 403;
};

const isExpectedAnonymousValidationFailure = (error) => {
    const status = Number(error?.response?.status || 0);
    if (status !== 401) return false;
    const hasAuthToken = Boolean(normalizeToken(getStoredAuthToken()));
    const hasSuperAdminToken = Boolean(normalizeToken(getStoredSuperAdminToken()));
    return !hasAuthToken && !hasSuperAdminToken && !hasActiveDispatchSession();
};

const getStoredUserSnapshot = () => {
    try {
        return getStoredUser();
    } catch {
        return null;
    }
};

const isPlatformSuperAdminUser = (userData) => Boolean(
    userData?.is_superadmin === true &&
    userData?.companyId == null &&
    userData?.company_id == null
);

const resolveCompanyIdFromSession = (userData, authToken) => {
    let raw = userData?.company_id ?? userData?.companyId ?? userData?.companyID ?? userData?.company ?? null;
    if (!raw && authToken) {
        const payload = decodeJwtPayload(authToken);
        raw = payload?.company_id ?? payload?.companyId ?? null;
    }
    const parsed = Number.parseInt(String(raw || ''), 10);
    return Number.isFinite(parsed) ? parsed : null;
};

const validateStoredDispatchSession = async (userData, authToken) => {
    const companyId = resolveCompanyIdFromSession(userData, authToken);
    if (!companyId || !authToken) {
        return false;
    }

    try {
        await axios.get(`/api/companies/${companyId}/vehicle-stats`, {
            timeout: 1500,
            skipAuthRetry: true,
            headers: {
                Authorization: `Bearer ${authToken}`
            }
        });
        return true;
    } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn('[AuthContext] Stored dispatch session validation failed:', error?.response?.status || error?.code || error?.message);
        }
        return false;
    }
};

const validateStoredSuperAdminSession = async (authToken) => {
    if (!authToken) {
        return false;
    }

    try {
        await axios.get('/api/superadmin/vehicle-stats', {
            timeout: 1500,
            skipAuthRetry: true,
            headers: {
                Authorization: `Bearer ${authToken}`
            }
        });
        return true;
    } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn('[AuthContext] Stored superadmin session validation failed:', error?.response?.status || error?.code || error?.message);
        }
        return false;
    }
};

const shouldDeferInitialAuthRender = () => {
    try {
        const storedToken = normalizeToken(getStoredAuthToken());
        const storedUser = getStoredUserSnapshot();
        const hasValidStoredSession = Boolean(storedToken && storedUser && !isExpiredToken(storedToken));
        const storedSuperAdminToken = normalizeToken(getStoredSuperAdminToken());
        const hasValidStoredSuperAdminSession = Boolean(
            storedSuperAdminToken &&
            storedUser &&
            isPlatformSuperAdminUser(storedUser) &&
            !isExpiredToken(storedSuperAdminToken)
        );

        return hasValidStoredSession || hasValidStoredSuperAdminSession;
    } catch {
        return false;
    }
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(() => shouldDeferInitialAuthRender());
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    axios.defaults.withCredentials = true;

    const clearClientAuthState = () => {
        clearAllAuthSessionFlags();
        clearStoredDispatchAuthData();
        clearStoredSuperAdminAuthData();
        clearImpersonationSession();
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
    };

    const applyAuthenticatedState = (userData, authToken = null) => {
        const cleanToken = normalizeToken(authToken);
        setStoredUser(userData);
        if (cleanToken) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`;
            setStoredAuthToken(cleanToken);
        } else {
            delete axios.defaults.headers.common['Authorization'];
        }
        markDispatchSessionActive();
        if (userData?.is_superadmin === true && (userData?.companyId == null && userData?.company_id == null)) {
            markSuperAdminSessionActive();
        } else {
            clearSuperAdminSessionActive();
            clearStoredSuperAdminAuthData();
        }
        setToken(cleanToken);
        setUser(userData);
        setIsAuthenticated(true);
    };

    // Visa boot-skärm bara om det faktiskt finns en giltig lagrad session att återställa.
    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            try {
                const storedToken = normalizeToken(getStoredAuthToken());
                const storedUser = getStoredUserSnapshot();
                const hasValidStoredSession = Boolean(storedToken && storedUser && !isExpiredToken(storedToken));
                const storedSuperAdminToken = normalizeToken(getStoredSuperAdminToken());
                const hasValidStoredSuperAdminSession = Boolean(
                    storedSuperAdminToken &&
                    storedUser &&
                    isPlatformSuperAdminUser(storedUser) &&
                    !isExpiredToken(storedSuperAdminToken)
                );

                if (cancelled) {
                    return;
                }

                if (hasValidStoredSuperAdminSession) {
                    const superAdminSessionValid = await validateStoredSuperAdminSession(storedSuperAdminToken);
                    if (cancelled) {
                        return;
                    }
                    if (!superAdminSessionValid) {
                        clearClientAuthState();
                        return;
                    }
                    applyAuthenticatedState(storedUser, storedSuperAdminToken);
                    return;
                }

                if (hasValidStoredSession) {
                    const dispatchSessionValid = await validateStoredDispatchSession(storedUser, storedToken);
                    if (cancelled) {
                        return;
                    }
                    if (!dispatchSessionValid) {
                        clearClientAuthState();
                        return;
                    }
                    applyAuthenticatedState(storedUser, storedToken);
                    return;
                }

                clearClientAuthState();
            } catch (error) {
                if (!cancelled) {
                    console.warn('[AuthContext] Bootstrap auth restore failed:', error?.response?.status || error?.code || error?.message);
                    clearClientAuthState();
                }
            }
        };

        init().finally(() => {
            if (!cancelled) {
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, []);

    const login = (userData, authToken) => {
        const cleanToken = normalizeToken(authToken);
        if (!cleanToken) {
            logout();
            return;
        }
        clearImpersonationSession();
        applyAuthenticatedState(userData, cleanToken);
        
        if (process.env.NODE_ENV !== 'production') {
            console.log('[AuthContext] User logged in:', userData.email);
        }
    };

    const logout = () => {
        const current = normalizeToken(getStoredAuthToken());
        if (current) {
            try {
                axios.post('/api/auth/logout', null, {
                    headers: { Authorization: `Bearer ${current}` }
                }).catch(() => {});
            } catch {}
        }
        clearClientAuthState();
        
        // Dispatch custom event for modal cleanup
        window.dispatchEvent(new Event('logout'));
        
        if (process.env.NODE_ENV !== 'production') {
            console.log('[AuthContext] User logged out');
        }
    };

    const updateUser = (updates) => {
        const updatedUser = { ...user, ...updates };
        setUser(updatedUser);
        setStoredUser(updatedUser);
    };

    const value = {
        user,
        token,
        isAuthenticated,
        loading,
        login,
        logout,
        updateUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
